/**
 * Set an account's current balance (FE-3, PHASE1 §4 `POST /accounts/{id}/balance`).
 * Writes a CLBD snapshot for the given day and mirrors `current_balance`. Only
 * available for manual accounts (`is_manual` — non-manual → server `manual_only` 409).
 *
 * The stateful body is mounted only while open, so state initialises fresh from
 * the current `account` prop each time (no reset effect).
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { ApiRequestError } from '../../lib/api.ts';
import { parseAmountInput } from '../../lib/format.ts';
import { useSetAccountBalance } from '../../lib/hooks/useAccountMutations.ts';
import type { AccountOut } from '../../lib/types.ts';

function todayIso(): string {
  return new Date().toLocaleDateString('sv-SE');
}

export interface BalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: AccountOut | null;
}

export function BalanceDialog({ open, onOpenChange, account }: BalanceDialogProps) {
  if (!open || !account) return null;
  return <BalanceForm account={account} onClose={() => onOpenChange(false)} />;
}

function BalanceForm({ account, onClose }: { account: AccountOut; onClose: () => void }) {
  const setBalance = useSetAccountBalance();
  const [amount, setAmount] = useState(() => account.current_balance ?? '');
  const [date, setDate] = useState(() => todayIso());
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    setError(null);
    const value = parseAmountInput(amount);
    if (value === null) {
      setError('Bitte einen gültigen Saldo angeben (z. B. -1234,56).');
      return;
    }
    setBalance.mutate(
      { id: account.id, body: { current_balance: value, balance_date: date || null } },
      {
        onSuccess: onClose,
        onError: (err) =>
          setError(
            err instanceof ApiRequestError
              ? err.message
              : 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
          ),
      },
    );
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Saldo setzen"
      description={account.name}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={setBalance.isPending}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={setBalance.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />

        <Field label="Aktueller Saldo" required hint="Negativ = Soll (z. B. Kreditkarten-Saldo).">
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              autoFocus
            />
          )}
        </Field>

        <Field label="Datum">
          {(id) => (
            <Input id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          )}
        </Field>
      </div>
    </Modal>
  );
}
